import {isSelectiveAiDomains} from 'common/HelperFunctions'
import {ToastContent, ToastOptions, toast} from 'react-toastify'

/**
 * Error toasts stay on screen long enough to be read.
 *
 * The ToastContainer's `autoClose` (3s) is right for "Saved" style confirmations but too
 * short for diagnostic errors — "Failed to add 1 constraint(s): Cannot mark Sunday as an
 * off day as it already has school timings defined." is gone before it can be read. With
 * ~1,150 `toast.error(...)` call sites, the duration is set here, once, instead of per call:
 * `installErrorToastDuration()` wraps `toast.error` so every error gets a length-scaled
 * `autoClose` unless the caller passed its own.
 */

/** What the ToastContainer uses for non-error toasts (kept in sync with App.tsx). */
export const DEFAULT_TOAST_AUTO_CLOSE_MS = 3000
/** Floor for any error — even a short one deserves a second look. */
export const ERROR_TOAST_MIN_MS = 5000
/** Ceiling so a wall of text doesn't sit there for half a minute; hover pauses it anyway. */
export const ERROR_TOAST_MAX_MS = 12000
/**
 * ~55ms per character ≈ 18 chars/s ≈ 200 words/min, a comfortable on-screen reading pace
 * for an unfamiliar sentence.
 */
const READ_MS_PER_CHAR = 55

const contentLength = (content: unknown): number => {
  if (typeof content === 'string') return content.length
  // Error objects are often passed straight through (`toast.error(err.message ?? …)` is
  // common, but so is `toast.error(err)` for strings-in-disguise).
  if (content instanceof Error) return content.message.length
  return 0
}

/** How long an error toast with this content should stay up, in ms. */
export const errorToastDuration = (content: unknown): number => {
  const length = contentLength(content)
  if (length === 0) return ERROR_TOAST_MIN_MS
  return Math.min(ERROR_TOAST_MAX_MS, Math.max(ERROR_TOAST_MIN_MS, length * READ_MS_PER_CHAR))
}

let installed = false

/**
 * Make every `toast.error(...)` in the app use `errorToastDuration()` for its `autoClose`
 * unless the call passes an explicit `autoClose`. Call once at bootstrap (App.tsx).
 *
 * Patching the property on the `toast` object is enough: call sites do `toast.error(...)`
 * at call time, so they pick up the wrapper. `toast(msg, {type: 'error'})` and `toast.update`
 * are not covered — they are rare and usually set their own timing.
 */
export const installErrorToastDuration = (): void => {
  if (installed) return
  installed = true
  // Selective-AI (Da1TimeTable) tenants only — the other portals keep the 3s default.
  if (!isSelectiveAiDomains()) return
  const original = toast.error
  toast.error = (<TData = unknown>(content: ToastContent<TData>, options?: ToastOptions<TData>) =>
    original(content, {autoClose: errorToastDuration(content), ...options})) as typeof toast.error
}
